import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, Modal } from 'antd';
import { LogoutOutlined, UserOutlined, ExclamationCircleOutlined, DownOutlined, MenuOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { getCurrentUser, logout as rbacLogout } from '../utils/rbac';
import './AdminHeader.css';

const { confirm } = Modal;

interface AdminHeaderProps {
  isMobile?: boolean;
  sidebarCollapsed?: boolean;
  onOpenSidebar?: () => void;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({ isMobile = false, sidebarCollapsed = false, onOpenSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const [time, setTime] = useState({ time: '', date: '' });

  useEffect(() => {
    const sync = () => setCurrentUser(getCurrentUser());
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  useEffect(() => {
    const format = () => {
      const now = new Date();
      setTime({
        time: now.toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        date: now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
      });
    };
    format();
    const id = setInterval(format, 1000);
    return () => clearInterval(id);
  }, []);

  const roleLabel = (currentUser?.roleName || 'User').trim();

  // Get page title from route
  const getPageTitle = (): string => {
    const path = location.pathname;
    const routeTitles: { [key: string]: string } = {
      '/admin-portal/dashboard': `${roleLabel} Dashboard`,
      '/admin-portal': `${roleLabel} Dashboard`,
      '/admin-portal/profile': 'My Profile',
      '/admin-portal/users': 'User Management',
      '/admin-portal/users/add': 'User Management',
      '/admin-portal/users/overview': 'User Management',
      '/admin-portal/users/privileges': 'User Management',
      '/admin-portal/locations': 'Location Management',
      '/admin-portal/purchase-orders': 'Purchase Orders',
      '/admin-portal/inventory': 'Inventory',
      '/admin-portal/reports/sales': 'Sales Report',
      '/admin-portal/reports/gst': 'GST Filing',
      '/admin-portal/reports/tds': 'TDS Withheld',
      '/admin-portal/reports/gl': 'General Ledger',
    };

    // Check for exact match first
    if (routeTitles[path]) {
      return routeTitles[path];
    }

    // Check for partial matches
    for (const [route, title] of Object.entries(routeTitles)) {
      if (path.startsWith(route)) {
        return title;
      }
    }

    return `${roleLabel} Dashboard`;
  };

  const getBreadcrumb = (): string => {
    return `Home / ${roleLabel} Dashboard`;
  };

  // Get user initials for avatar
  const getUserInitials = (): string => {
    if (currentUser?.name) {
      const names = currentUser.name.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[1][0]).toUpperCase();
      }
      return currentUser.name.substring(0, 2).toUpperCase();
    }
    const parts = roleLabel.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return roleLabel.substring(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    confirm({
      title: 'Are you sure you want to logout?',
      icon: <ExclamationCircleOutlined />,
      content: 'You will be redirected to the login page.',
      okText: 'Yes, Logout',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        rbacLogout();
        // Admins go to Admin Portal login; all others (Manager, etc.) go to Company User Portal
        if (roleLabel.toLowerCase() === 'admin') {
          navigate('/admin/login');
        } else {
          navigate('/user/login');
        }
      },
    });
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
    },
  ];

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      handleLogout();
    } else if (key === 'profile') {
      navigate('/admin-portal/profile');
    }
  };

  return (
    <div className="admin-header">
      <div className="admin-header-left">
        {isMobile && (
          <button
            type="button"
            className="admin-header-menu-btn"
            onClick={onOpenSidebar}
            aria-label="Open sidebar"
          >
            <MenuOutlined />
          </button>
        )}
        <div className="admin-header-breadcrumb-wrap">
          <span className="admin-header-breadcrumb-text">Home</span>
          <span className="admin-header-breadcrumb-sep"> / </span>
          <span className="admin-header-breadcrumb-current">{getPageTitle()}</span>
        </div>
      </div>
      <div className="admin-header-right">
        <div className="admin-header-datetime">
          <div className="admin-header-time">{time.time}</div>
          <div className="admin-header-date">{time.date}</div>
        </div>
        <Dropdown
          menu={{
            items: userMenuItems,
            onClick: handleUserMenuClick,
          }}
          placement="bottomRight"
          arrow
        >
          <button type="button" className="admin-header-user-pill">
            <div className="admin-avatar-icon admin-avatar-icon--pill">{getUserInitials()}</div>
            <span className="admin-header-user-name">{(currentUser?.name || roleLabel).trim()}</span>
            <DownOutlined className="admin-header-chevron" />
          </button>
        </Dropdown>
      </div>
    </div>
  );
};

export default AdminHeader;
